import React from 'react';
import { TrendingUp, Users, FileVideo, MessageSquare, UserPlus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const AnalyticsDashboard = ({ stats }) => {
  if (!stats) {
    return <div className="text-center py-20 text-gray-400">Loading analytics...</div>;
  }

  const statCards = [
    {
      title: 'Total Users',
      value: stats.total_users,
      icon: <Users className="w-8 h-8 text-blue-400" />,
      color: 'from-blue-500 to-cyan-500',
      description: 'Registered users'
    },
    {
      title: 'Active Users',
      value: stats.active_users,
      icon: <TrendingUp className="w-8 h-8 text-green-400" />,
      color: 'from-green-500 to-emerald-500',
      description: 'Currently active'
    },
    {
      title: 'Total Content',
      value: stats.total_content,
      icon: <FileVideo className="w-8 h-8 text-purple-400" />,
      color: 'from-purple-500 to-pink-500',
      description: 'Uploaded files'
    },
    {
      title: 'Messages',
      value: stats.total_messages,
      icon: <MessageSquare className="w-8 h-8 text-yellow-400" />,
      color: 'from-yellow-500 to-orange-500',
      description: 'Total messages'
    },
    {
      title: 'Recent Sign-ups',
      value: stats.recent_registrations,
      icon: <UserPlus className="w-8 h-8 text-indigo-400" />,
      color: 'from-indigo-500 to-purple-500',
      description: 'Last 7 days'
    },
    {
      title: 'Inactive Users',
      value: stats.inactive_users,
      icon: <Users className="w-8 h-8 text-gray-400" />,
      color: 'from-gray-500 to-slate-500',
      description: 'Deactivated accounts'
    },
  ];

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-white mb-2">Platform Analytics</h2>
        <p className="text-gray-400">Overview of I Got Soul platform metrics</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat) => (
          <Card
            key={stat.title}
            className="bg-gray-800/50 border-gray-700 hover:border-gray-600 transition-all hover:scale-105"
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-gray-400 text-sm font-medium">
                  {stat.title}
                </CardTitle>
                <div className={`p-3 rounded-lg bg-gradient-to-r ${stat.color} bg-opacity-20`}>
                  {stat.icon}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-white mb-2">
                {stat.value.toLocaleString()}
              </div>
              <p className="text-sm text-gray-500">{stat.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Additional Insights */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">User Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Active Rate</span>
                <span className="text-white font-semibold">
                  {((stats.active_users / stats.total_users) * 100).toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-green-400 to-green-600 h-2 rounded-full"
                  style={{ width: `${(stats.active_users / stats.total_users) * 100}%` }}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Growth Metrics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-400">New Users (7 days)</span>
                <span className="text-white font-semibold">
                  {stats.recent_registrations}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Avg. per day</span>
                <span className="text-green-400 font-semibold">
                  +{(stats.recent_registrations / 7).toFixed(1)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
